// src/ChatPage.tsx
import React, { useEffect, useRef, useState } from "react";
import { useChatApi } from "../api/useChatApi";
import { MessageList } from "./MessageList";
import { ChatInput } from "./ChatInput";
import { AppFooter } from "./AppFooter";
import { RateLimitMessage } from "./RateLimitMessage";
import { makeStyles, tokens } from "@fluentui/react-components";
import HeroWelcome from "./HeroWelcome";
import { getBrandConfig } from "../brands/brand-registry";
import { useScreenReaderAnnouncements } from "../hooks/useScreenReaderAnnouncements";
import { InputMode } from "../api/models";
import { stopAllAudioPlayback } from "../services/audioService";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
  },
  ariaLive: {
    position: "absolute",
    left: "-10000px",
    width: "1px",
    height: "1px",
    overflow: "hidden",
  },
  contentWrapper: {
    maxWidth: '1080px',
    marginInline: 'auto',
    width: '100%',
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    boxSizing: "border-box",
    '@media (max-width: 600px)': {
      paddingLeft: tokens.spacingHorizontalXS,
      paddingRight: tokens.spacingHorizontalXS,
    },
  },
  messageList: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    paddingTop: "1rem",
    paddingBottom: "1rem",
    // Hide scrollbar but keep scrolling functionality
    scrollbarWidth: "none", // Firefox
    msOverflowStyle: "none", // IE and Edge
    "&::-webkit-scrollbar": {
      display: "none", // Chrome, Safari, Opera
    },
    '@media (max-width: 600px)': {
      paddingTop: tokens.spacingVerticalXS,
      paddingBottom: tokens.spacingVerticalXS,
    },
  },
  chatInput: {
    flex: "0 0 auto",
  },
  chatInputDefault: {
    borderTop: 'var(--chat-input-container-border-top)',
    backgroundColor: 'var(--chat-input-container-bg)'
  },
  chatInputFloating: {
    borderTop: 'var(--chat-input-container-border-top)',
    backgroundColor: 'var(--chat-input-container-bg)',
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
  },
  footer: {
    flex: "0 0 auto",
  },
  footerDefault: {
    borderTop: 'var(--footer-border-top)',
  },
  footerGhost: {
    borderTop: 'var(--footer-border-top)',
  },
});

export const ChatPage: React.FC = () => {
  const styles = useStyles();
  const brandConfig = getBrandConfig();
  const { 
    messages, 
    sendMessage, 
    clearContext, 
    isProcessing,
    rateLimitInfo,
    isRateLimited,
    handleRateLimitRetry,
    handleRateLimitError
  } = useChatApi();
  const [pendingQuestion, setPendingQuestion] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // WCAG 2.1 Level AA: Screen reader announcements (extracted to custom hook)
  const { ariaLiveMessage } = useScreenReaderAnnouncements(messages);
  
  // Get layout preferences from brand config
  const footerStyle = brandConfig.layout.footerStyle || 'default';
  const hideBottomInputOnWelcome = Boolean(brandConfig.layout.hideBottomInputOnWelcome);
  const showWelcomeInput = Boolean(brandConfig.hero.showInputField ?? true);
  const welcomeInputPosition = brandConfig.hero.inputFieldPosition ?? 'top';
  
  // Determine which input should receive focus on welcome screen
  // Priority: HeroWelcome input (if shown) > ChatInput (if shown)
  const shouldFocusChatInput = messages.length === 0 && 
                                !hideBottomInputOnWelcome && 
                                (!showWelcomeInput || welcomeInputPosition === 'bottom');

  const handleSend = (userQuery: string, isBriefResponse: boolean, inputMode: InputMode = "Text") => {
    sendMessage(userQuery, isBriefResponse, inputMode);
  };

  const handleFollowUpClick = (question: string, inputMode: InputMode = "Text") => {
    // Always update the pending question (overrides previous)
    setPendingQuestion(question);
    
    // Auto-submit if no other question is being processed
    if (!isProcessing()) {
      // Respect the toggle state for follow-up questions too
      let briefResponse = false;
      if (brandConfig.features.enableBriefResponse) {
        const storedBrief = localStorage.getItem('briefResponse');
        briefResponse = storedBrief !== null && storedBrief === 'true';
      }
      sendMessage(question, briefResponse, inputMode);
      // Clear textarea after auto-submit with a small delay to ensure the question was sent
      setTimeout(() => setPendingQuestion(""), 100);
    }
  };

  const handleClearContext = () => {
    stopAllAudioPlayback();
    clearContext();
    setPendingQuestion("");
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "nearest",
    });
  }, [messages]);

  return (
    <div className={styles.container}>
      {/* WCAG 2.1 Level AA: Aria-live region for screen reader announcements */}
      <div 
        className={styles.ariaLive}
        role="status" 
        aria-live="polite" 
        aria-atomic="true"
      >
        {ariaLiveMessage}
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.messageList}>
          {messages.length === 0 && (
            <HeroWelcome onPick={handleFollowUpClick} />
          )}
          <MessageList
            messages={messages}
            onFollowUpClick={handleFollowUpClick}
            onRateLimitError={handleRateLimitError}
          />
          <div ref={messagesEndRef} />
        </div>
        {/* Rate limit message */}
        {isRateLimited && rateLimitInfo && (
          <div style={{ padding: '0 1rem' }}>
            <RateLimitMessage
              retryAfter={rateLimitInfo.retryAfter}
              onRetry={handleRateLimitRetry}
              showExtensionButton={false}
            />
          </div>
        )}
        {/* Conditionally hide chat input on welcome screen based on brand config */}
        {!(messages.length === 0 && hideBottomInputOnWelcome) && (
          <div className={`${styles.chatInput} ${
            brandConfig.layout.chatInputContainerStyle === 'floating' 
              ? styles.chatInputFloating 
              : styles.chatInputDefault
          }`}>
            <ChatInput
              value={pendingQuestion}
              onChange={setPendingQuestion}
              onSend={handleSend}
              onClearContext={handleClearContext}
              autoFocus={shouldFocusChatInput}
              isRateLimited={isRateLimited}
              disabled={isProcessing()}
            />
          </div>
        )}
        <div className={`${styles.footer} ${
          footerStyle === 'ghost' 
            ? styles.footerGhost 
            : styles.footerDefault
        }`}>
          <AppFooter />
        </div>
      </div>
    </div>
  );
};
